<script setup lang="ts">
import { Field, ErrorMessage } from 'vee-validate'
import { UserPlusIcon } from '@heroicons/vue/20/solid'
import ChatGptModelSelect from '@/components/projects/settings/ai/ChatGptModelSelect.vue'
import { computed, inject, onMounted, onUnmounted, ref, watch } from 'vue'
import { useTranslations } from '@/composables/useTranslations.ts'
import { GetProjectChatGPTModelsAndProjectFiles } from '@/components/projects/graphql/project.queries.ts'
import { useLazyQuery, useMutation, useQuery } from '@vue/apollo-composable'
import type {
  ChatGptAssistantFile, ChatGptResponsesApiForm,
  ChatGptResponsesApiInfo, ChatGptModel
} from '@/components/projects/interfaces.ts'
import { cloneDeep, isEqual, merge, omit, round, sortBy } from 'lodash'
import ChatGptAssistantFiles from '@/components/projects/settings/assistant/ChatGptAssistantFiles.vue'
import type { RangeSliderMinMaxStep } from '@/components/interfaces/project-settings-view.interfaces.ts'
import { BackendErrorCode, ProjectAIType, ProjectSettingsField } from '@/enums.ts'
import { useToast } from 'vue-toastification'
import {
  ProjectSettingsUpdateChatGptResponsesApiSettings,
} from '@/components/projects/graphql/project.mutations.ts'
import { isNumeric } from '@/utils/util.ts'
import { storeToRefs } from 'pinia'
import { useUserStore } from '@/stores/user.store.ts'
import { ProjectSettingFormKey } from '@/components/projects/settings.types.ts'

const { defineField, setFieldValue } = inject(ProjectSettingFormKey)
const props = defineProps<{
  projectId: number | null
}>()

const toast = useToast()
const { t } = useTranslations()
const isLoading = ref<boolean>(true)
const isAssistantCreated = ref(false)
const assistantKey = ref<string | null>(null)
const files = ref<ChatGptAssistantFile[]>([])
const chatGptModels = ref<ChatGptModel[]>([])
const form = ref<ChatGptResponsesApiForm>(getFormDefaultState())
const formInitial = ref<ChatGptResponsesApiInfo>(getFormDefaultState())
const hasChanges = computed<boolean>(() => {
  return !isEqual(form.value, formInitial.value)
})
const { hasPresentationModeEnabled } = storeToRefs(useUserStore())

// Variables
const variablesProjectId = ref<{ projectId: number }>( {
  projectId: props.projectId as number
})
// Queries & mutations
const {
  onError: onAssistantInfoLoadingFailed,
  onResult: onAssistantInfoReceived,
} = useQuery<{
  apiInfo: ChatGptResponsesApiInfo
  chatGptModels: ChatGptModel[]
  files: ChatGptAssistantFile[]
}>(GetProjectChatGPTModelsAndProjectFiles, { variables: variablesProjectId, fetchPolicy: 'cache-and-network' })
const {
  onError: onAssistantUpdateFailed,
  onDone: onChatGPTResponsesApiInfoUpdated,
  mutate: updateAssistant
} = useMutation<{
  apiInfo: ChatGptResponsesApiInfo
}>(ProjectSettingsUpdateChatGptResponsesApiSettings, { fetchPolicy: 'no-cache' })
const [ chatGptInitialTask ] = defineField(ProjectSettingsField.AiChatGptInitialTask)
const [ chatGptModel ] = defineField(ProjectSettingsField.AiChatGptModel)


onAssistantInfoReceived(({ apiInfo, chatGptModels, files }) => {
  // console.log('apiInfo:', apiInfo)
  console.log('chatGptModels: ', chatGptModels)
  console.log('files: ', files)

  setAssistantFormData(cloneDeep(apiInfo))
  setAssistantInitialFormData(cloneDeep(apiInfo))
  setChatGptModels(sortBy(cloneDeep(chatGptModels), 'ID'))
  setFiles(files)
  setAssistantCreated(true)
  setIsLoading(false)
})

onAssistantInfoLoadingFailed((error) => {
  setIsLoading(false)

  if (error.message !== BackendErrorCode.ErrChatGptAssistantNotCreated) {
    toast.error(error.message)
    console.error('onAssistantInfoLoadingFailed | Error', error)
  } else {
    setFiles([])
    setAssistantCreated(false)
    setAssistantFormData(getFormDefaultState())
    setAssistantInitialFormData(getFormDefaultState())
  }
})

onChatGPTResponsesApiInfoUpdated((result) => {
  const { apiInfo } = result.data as { apiInfo: ChatGptResponsesApiInfo }
  setAssistantFormData(apiInfo)
  toast.success('ChatGPT assistant updated!')
  setIsLoading(false)
})

onAssistantUpdateFailed((error) => {
  setIsLoading(false)
  toast.error(error.message)
  console.error('onSaveAssistantButtonClicked | onError:', error)
})

function getFormDefaultState (): ChatGptResponsesApiForm {
  return {
    model: '',
    instructions: '',
  }
}

function setAssistantCreated(flag: boolean): void {
  isAssistantCreated.value = flag
}

function setAssistantFormData(data: ChatGptResponsesApiForm): void {
  form.value = data
}

function setAssistantInitialFormData(data: ChatGptResponsesApiForm): void {
  formInitial.value = data
}


function setFiles(data: ChatGptAssistantFile[]) {
  files.value = data
}

function setChatGptModels(models: ChatGptModel[]): void {
  chatGptModels.value = models
}

function setIsLoading(flag: boolean): void {
  isLoading.value = flag
}


onMounted(async () => {
  const { stop: stopProjectIdPropWatcher } = watch(() => props.projectId, async (newValue) => {
    if (isNumeric(newValue)) {
      variablesProjectId.value.projectId = newValue as number
    }

  }, { immediate: true })

  onUnmounted(() => {
    stopProjectIdPropWatcher()
  })
})
</script>

<template>
  <div class="container mx-auto">
    <div v-if="isLoading" class="w-full text-center">
      <img src="/images/loading_spinner.gif" class="w-64 h-64 m-auto" />
    </div>
    <div v-else>
        <!-- Name and Model Section -->
        <div class="border-b border-gray-300 pb-4">
          <div class="grid grid-cols-1 gap-6 sm:grid-cols-6">
            <!-- Model Field -->
            <div class="sm:col-span-6">
              <Field :name="ProjectSettingsField.AiChatGptAssistantModel">
                <ChatGptModelSelect v-model="chatGptModel" :models="chatGptModels" />
                <hr />
              </Field>
            </div>
          </div>
        </div>
        <!-- System Instructions -->
        <div class="border-b border-gray-300 pb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">
            {{ t('settings.ai.assistant.systemInstructions') }}
          </label>
          <div>
            <Field :name="ProjectSettingsField.AiChatGptInitialTask" v-model="chatGptInitialTask" v-slot="{ field, meta }">
              <textarea
                rows="15"
                v-bind="field"
                :class="{
                  'block w-full rounded-lg border px-3 py-2 text-gray-900 shadow-xs focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm': true,
                  'border-red-500': meta.touched && !meta.valid,
                  'blurry-text': hasPresentationModeEnabled
                }"
              ></textarea>
            </Field>
          </div>
          <ErrorMessage :name="ProjectSettingsField.AiChatGptAssistantInstructions" class="mt-1 text-sm text-red-600" />
        </div>

        <!-- Tools Section -->
        <div class="border-gray-300">
          <ChatGptAssistantFiles
            :projectId="projectId"
            :files="files"
          />
          <!--<div v-if="files.length" class="mt-4">
            <h3 class="text-sm font-medium text-gray-700 mb-2">
              {{ t('settings.ai.assistant.uploadedFiles') }}:
            </h3>
            <ul class="list-disc pl-5">
              <li v-for="(file, index) in files" :key="index" class="text-sm text-gray-600">
                {{ file }}
              </li>
            </ul>
          </div>-->
        </div>

        <!-- Model Configuration -->

        <div class="pb-4 text-center">


        <!-- API Version -->
        <!--<div>
          <h2 class="text-lg font-semibold text-gray-600">
            {{ t('settings.ai.assistant.apiVersion') }}
          </h2>
          <div class="mt-4">
            <label class="block text-sm font-medium text-gray-700 mb-1">
              {{ t('settings.ai.assistant.latest') }}
            </label>
            <div class="text-sm text-gray-500">
              {{ t('settings.ai.assistant.updated') }} 2/17, 2:33 AM
            </div>
          </div>
        </div>-->
      </div>
    </div>
  </div>
</template>
